/**
 * Locales shipped in the app. Add codes here when adding `locales/<code>/*.json` + i18n resources.
 */
export const SUPPORTED_LOCALE_CODES = ['en', 'ta', 'de', 'fr', 'es', 'zh', 'ja', 'nl', 'ru'] as const;

export type AppLocale = (typeof SUPPORTED_LOCALE_CODES)[number];

/**
 * Locales we will *expose in the UI language switcher* during the public
 * beta. See `docs/SCOPE_DECISIONS.md §6` — only EN is fully reviewed for
 * launch; other locales remain in code but are hidden from end users until
 * a native-speaker pass lands.
 */
export const PUBLIC_BETA_LOCALE_CODES = ['en'] as const;

export type PublicBetaLocale = (typeof PUBLIC_BETA_LOCALE_CODES)[number];

export function isSupportedLocale(value: string): value is AppLocale {
  return (SUPPORTED_LOCALE_CODES as readonly string[]).includes(value);
}

export function isPublicBetaLocale(value: string): value is PublicBetaLocale {
  return (PUBLIC_BETA_LOCALE_CODES as readonly string[]).includes(value);
}

/** Normalize API / storage value to a supported UI locale; default English. */
export function normalizeLocale(value: unknown): AppLocale {
  if (typeof value === 'string' && isSupportedLocale(value)) {
    return value;
  }
  return 'en';
}

/**
 * Same as {@link normalizeLocale} but additionally collapses anything that
 * is not in the public-beta allow-list down to English. The language
 * switcher should call this before persisting a user's choice during the
 * public beta so we never flip the UI into a half-translated locale.
 */
export function normalizeBetaLocale(value: unknown): PublicBetaLocale {
  if (typeof value === 'string' && isPublicBetaLocale(value)) {
    return value;
  }
  return 'en';
}
